import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ErrorState, Spinner } from '../components/ui.jsx';
import { useFetch, useTitle } from '../lib/hooks.js';
import { formatDate, formatDateTime, formatXaf, titleCase } from '../lib/format.js';
import { saveElementAsPdf } from '../lib/pdf.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/**
 * A utility receipt, sized for A5 like the rent one. It carries the reading it
 * settles as well as the money, so the customer can match it back to the meter
 * without going online - which matters when they are being billed off somebody
 * else's dial.
 */
const PrintableUtilityReceipt = () => {
  const { id } = useParams();
  const { paymentMethods } = useMeta();
  const toast = useToast();
  const { data, loading, error, reload } = useFetch(`/utility-payments/${id}/receipt`, [id]);
  const receiptRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useTitle(data ? `Receipt ${data.receipt.receiptNumber}` : 'Utility receipt');

  useEffect(() => {
    document.body.classList.add('printing');
    return () => document.body.classList.remove('printing');
  }, []);

  const downloadPDF = async () => {
    if (!receiptRef.current || !data || saving) return;

    setSaving(true);
    try {
      await saveElementAsPdf(receiptRef.current, {
        margin: 5,
        filename: `receipt-${data.receipt.receiptNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a5', orientation: 'portrait' }
      });
    } catch (cause) {
      console.error('Utility receipt PDF failed:', cause);
      toast.error('Could not build the PDF. Use the Print button and save as PDF instead.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const { receipt, bill, account, service, customer, landlord, platform } = data;
  const methodLabel = paymentMethods.find((method) => method.slug === receipt.method)?.label || titleCase(receipt.method);
  const balance = receipt.balanceAfter;

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <Link to="/landlord/utilities" className="btn btn-ghost btn-sm">
          Back
        </Link>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
          Print
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={saving}>
          {saving ? 'Preparing PDF...' : '↓ Download PDF'}
        </button>
      </div>

      <article ref={receiptRef} className={`receipt-sheet utility-receipt-sheet ${receipt.voided ? 'is-void' : ''}`}>
        {receipt.voided ? <div className="void-stamp">Cancelled</div> : null}

        <header className="receipt-head">
          <div>
            <h1>Utility receipt</h1>
            <p className="receipt-number">{receipt.receiptNumber}</p>
          </div>
          <div className="receipt-issuer">
            {landlord.logoUrl ? <img className="doc-logo" src={landlord.logoUrl} alt="" /> : null}
            <strong>{landlord.name}</strong>
            {landlord.phone ? <span>{landlord.phone}</span> : null}
            <span className="muted">{platform.name}</span>
          </div>
        </header>

        <table className="receipt-body">
          <tbody>
            <tr>
              <th scope="row">Received from</th>
              <td>
                <strong>{customer.name}</strong>
                {customer.phone ? <span className="muted"> {customer.phone}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">The sum of</th>
              <td>
                <strong className="receipt-amount">{formatXaf(receipt.amount)}</strong>
              </td>
            </tr>
            <tr>
              <th scope="row">Being for</th>
              <td>
                {service.name}
                {bill.billingMode === 'metered' ? (
                  <span className="muted">
                    {' '}
                    - {bill.unitsUsed} {service.unitLabel} at {formatXaf(bill.unitCost)} per {service.unitLabel}
                  </span>
                ) : (
                  <span className="muted"> - fixed charge</span>
                )}
              </td>
            </tr>
            <tr>
              <th scope="row">Against bill</th>
              <td>
                {bill.reference}
                {bill.periodStart ? (
                  <span className="muted">
                    {' '}
                    - {formatDate(bill.periodStart)} to {formatDate(bill.periodEnd)}
                  </span>
                ) : null}
              </td>
            </tr>
            {bill.billingMode === 'metered' ? (
              <tr>
                <th scope="row">Meter</th>
                <td>
                  {account.meterNumber ? `${account.meterNumber} - ` : ''}
                  read {bill.previousReading} to {bill.currentReading} on {formatDate(bill.readOn)}
                </td>
              </tr>
            ) : null}
            <tr>
              <th scope="row">Paid by</th>
              <td>
                {methodLabel}
                {receipt.reference ? <span className="muted"> - reference {receipt.reference}</span> : null}
              </td>
            </tr>
            <tr>
              <th scope="row">Date received</th>
              <td>{formatDate(receipt.paidAt)}</td>
            </tr>
            <tr>
              <th scope="row">Utility balance after this payment</th>
              <td>
                {balance > 0
                  ? `${formatXaf(balance)} still owing`
                  : balance < 0
                    ? `${formatXaf(-balance)} paid in advance`
                    : 'Nothing owing'}
              </td>
            </tr>
          </tbody>
        </table>

        <footer className="receipt-foot">
          <div className="receipt-sign">
            <span className="receipt-sign-line" />
            <span>For {landlord.name}</span>
          </div>
          <div className="receipt-meta">
            <span>Issued {formatDateTime(receipt.createdAt)}</span>
            {receipt.recordedByName ? <span>Recorded by {receipt.recordedByName}</span> : null}
            <span className="muted">
              This receipt was generated by {platform.name} and can be verified against the utility statement of both
              parties.
            </span>
          </div>
        </footer>

        {receipt.voided ? (
          <p className="receipt-void-note">
            This receipt was cancelled{receipt.voidReason ? `: ${receipt.voidReason}` : '.'} It no longer counts towards
            what has been paid.
          </p>
        ) : null}
      </article>
    </div>
  );
};

export default PrintableUtilityReceipt;
